import React, { useState } from 'react';
import { Outlet, useNavigate, useLocation, Link } from 'react-router-dom';
import { useWalletAuth } from '../context/WalletAuthContext';
import { Home, Wallet, Gift, Receipt, User, LogOut, ChevronRight, Bell, Settings, Menu, X, FileText } from 'lucide-react';
import { Button } from '../components/ui/button';

const NAV_ITEMS = [
  { icon: Home, label: 'Home', path: '/app/employee/home' },
  { icon: Wallet, label: 'Wallet', path: '/app/employee/wallet', children: [
    { label: 'Balance', path: '/app/employee/wallet' },
    { label: 'Send', path: '/app/employee/wallet/send' },
  ]},
  { icon: Receipt, label: 'My Pay', path: '/app/employee/pay' },
  { icon: Gift, label: 'Benefits', path: '/app/employee/benefits', children: [
    { label: 'My Plan', path: '/app/employee/benefits' },
    { label: 'Vendors', path: '/app/employee/benefits/vendors' },
  ]},
  { icon: FileText, label: 'Offer Letters', path: '/app/employee/offer-letters' },
  { icon: Gift, label: 'Flex Benefits', path: '/app/employee/flex-benefits' },
  { icon: Receipt, label: 'Transactions', path: '/app/employee/transactions' },
  { icon: User, label: 'Profile', path: '/app/employee/profile' },
];

export default function EmployeeAppLayout() {
  const { logout } = useWalletAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [expandedNav, setExpandedNav] = useState(null);

  const handleLogout = () => {
    logout();
  };

  const user = JSON.parse(localStorage.getItem('wallet_user') || '{}');
  const isActive = (path) => location.pathname === path || (path !== '/app/employee/home' && location.pathname.startsWith(path) && path.length > 16);

  return (
    <div className="min-h-screen bg-gray-50 flex">
      {/* Desktop Sidebar */}
      <aside className="hidden lg:flex flex-col w-56 bg-white border-r border-gray-200 fixed h-full z-20">
        {/* Logo */}
        <div className="px-5 py-5 border-b border-gray-100">
          <Link to="/" className="text-lg font-bold text-gray-900" style={{ fontFamily: 'JetBrains Mono' }}>
            <span className="text-pink-500">pebbles</span>
          </Link>
        </div>

        {/* Nav */}
        <nav className="flex-1 py-3 px-3 space-y-0.5 overflow-y-auto">
          {NAV_ITEMS.map(item => (
            <div key={item.path}>
              <button
                onClick={() => {
                  if (item.children) {
                    setExpandedNav(expandedNav === item.path ? null : item.path);
                  }
                  navigate(item.children ? item.children[0].path : item.path);
                }}
                className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-xs font-medium transition-colors ${
                  isActive(item.path) ? 'bg-pink-50 text-pink-600' : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                }`}
              >
                <item.icon className="w-4 h-4" />
                {item.label}
              </button>
              {item.children && (expandedNav === item.path || isActive(item.path)) && (
                <div className="ml-7 mt-0.5 space-y-0.5">
                  {item.children.map(child => (
                    <button key={child.path} onClick={() => navigate(child.path)}
                      className={`w-full text-left px-3 py-1.5 rounded-md text-[11px] font-medium ${
                        location.pathname === child.path ? 'text-pink-600 bg-pink-50' : 'text-gray-400 hover:text-gray-600'
                      }`}>
                      {child.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          ))}
        </nav>

        {/* User footer */}
        <div className="p-3 border-t border-gray-100">
          <div className="flex items-center gap-2 px-2 py-1.5">
            <div className="w-7 h-7 rounded-full bg-pink-50 text-pink-600 flex items-center justify-center text-[10px] font-bold">
              {user.first_name?.[0]}{user.last_name?.[0]}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[11px] font-medium text-gray-900 truncate">{user.first_name} {user.last_name}</p>
              <p className="text-[10px] text-gray-400 truncate">{user.email}</p>
            </div>
            <button onClick={handleLogout} className="text-gray-400 hover:text-red-500"><LogOut className="w-3.5 h-3.5" /></button>
          </div>
        </div>
      </aside>

      {/* Mobile Header & Bottom Tabs */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-20 bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between">
        <Link to="/" className="text-base font-bold" style={{ fontFamily: 'JetBrains Mono' }}><span className="text-pink-500">pebbles</span></Link>
        <div className="flex items-center gap-2">
          <button className="w-8 h-8 rounded-full bg-gray-50 flex items-center justify-center text-gray-500"><Bell className="w-4 h-4" /></button>
          <button onClick={handleLogout} className="w-8 h-8 rounded-full bg-gray-50 flex items-center justify-center text-gray-500"><LogOut className="w-3.5 h-3.5" /></button>
        </div>
      </div>

      {/* Mobile Bottom Tabs */}
      <nav className="lg:hidden fixed bottom-0 left-0 right-0 z-20 bg-white border-t border-gray-200 flex justify-around py-1.5 px-2">
        {[
          { icon: Home, label: 'Home', path: '/app/employee/home' },
          { icon: Wallet, label: 'Wallet', path: '/app/employee/wallet' },
          { icon: Gift, label: 'Benefits', path: '/app/employee/benefits' },
          { icon: Receipt, label: 'Activity', path: '/app/employee/transactions' },
          { icon: User, label: 'Profile', path: '/app/employee/profile' },
        ].map(item => (
          <button key={item.path} onClick={() => navigate(item.path)}
            className={`flex flex-col items-center gap-0.5 py-1 px-2 rounded-lg ${isActive(item.path) ? 'text-pink-500' : 'text-gray-400'}`}>
            <item.icon className="w-4.5 h-4.5" />
            <span className="text-[10px] font-medium">{item.label}</span>
          </button>
        ))}
      </nav>

      {/* Main Content */}
      <main className="flex-1 lg:ml-56 min-h-screen">
        <div className="max-w-3xl mx-auto px-4 pt-16 lg:pt-8 pb-20 lg:pb-8">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
